import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import styled from "styled-components";

import { Button, ButtonActionType, ButtonType } from "@ds/components/forms/buttons";
import { Checkbox, Dropdown, InputText } from "@ds/components/forms/inputs";
import { IconType } from "@ds/components/icons";

import { type TableFilterDefinition, type TableFilterOption } from "./types";

const Wrapper = styled.div`
  width: 320px;
  display: flex;
  flex-flow: column;
  gap: 8px;
`;

const Title = styled.div`
  font-weight: 600;
`;

const FieldButton = styled.button`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 8px;
  border: none;
  border-radius: 6px;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--primaryColor010, #f3f4f6);
  }
`;

const InUse = styled.span`
  color: var(--textColor060);
  font-size: 0.85rem;
`;

const Options = styled.div`
  max-height: 220px;
  overflow-y: auto;
  display: flex;
  flex-flow: column;
  gap: 6px;
`;

const Option = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const Hint = styled.div<{ $error?: boolean }>`
  color: ${({ $error }) => ($error ? "var(--red-color)" : "var(--textColor060)")};
  font-size: 0.85rem;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const TEXT_OPERATORS: { value: TableFilterOperator; label: string }[] = [
  { value: "starts", label: "starts with" },
  { value: "is", label: "is" },
];

const useFilterOptions = (definition: TableFilterDefinition) => {
  const [remoteOptions, setRemoteOptions] = useState<TableFilterOption[]>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (definition.kind !== "remote-select") {
      return;
    }

    let isCancelled = false;
    definition
      .fetchOptions()
      .then(values => !isCancelled && setRemoteOptions(values.map(value => ({ value, label: value }))))
      .catch(() => !isCancelled && setError("Couldn't load values"));

    return () => {
      isCancelled = true;
    };
  }, [definition]);

  if (definition.kind === "select") {
    return { options: definition.options };
  }

  return { options: remoteOptions, error };
};

const FieldPicker: FC<{
  definitions: TableFilterDefinition[];
  tableFilters: TableFilters;
  onPick: (field: string) => void;
}> = ({ definitions, tableFilters, onPick }) => (
  <Wrapper>
    <Title>Filter by</Title>
    {definitions.map(({ field, label }) => (
      <FieldButton key={field} type="button" onClick={() => onPick(field)}>
        {label}
        {tableFilters[field] && <InUse>in use, edit</InUse>}
      </FieldButton>
    ))}
  </Wrapper>
);

const SelectValues: FC<{
  definition: TableFilterDefinition;
  values: string[];
  onChange: (values: string[]) => void;
}> = ({ definition, values, onChange }) => {
  const { options, error } = useFilterOptions(definition);
  const [search, setSearch] = useState("");

  const visibleOptions = useMemo(
    () => options?.filter(({ label }) => label.toLowerCase().includes(search.toLowerCase())),
    [options, search],
  );

  const toggle = useCallback(
    (value: string) => onChange(values.includes(value) ? values.filter(v => v !== value) : [...values, value]),
    [values, onChange],
  );

  if (error) {
    return <Hint $error>{error}</Hint>;
  }

  if (!visibleOptions) {
    return <Hint>Loading values…</Hint>;
  }

  return (
    <>
      {definition.kind === "remote-select" && (
        <InputText value={search} placeholder="Search values..." onChange={({ target }) => setSearch(target.value)} />
      )}
      <Options>
        {visibleOptions.map(({ value, label }) => (
          <Option key={value}>
            <Checkbox checked={values.includes(value)} onChange={() => toggle(value)} />
            {label}
          </Option>
        ))}
        {!visibleOptions.length && <Hint>No values</Hint>}
      </Options>
    </>
  );
};

const ValueEditor: FC<{
  definition: TableFilterDefinition;
  initialValue?: TableFilterValue;
  onBack: () => void;
  onCancel: () => void;
  onApply: (value: TableFilterValue) => void;
}> = ({ definition, initialValue, onBack, onCancel, onApply }) => {
  const isText = definition.kind === "text";
  const [operator, setOperator] = useState<TableFilterOperator>(initialValue?.operator ?? (isText ? "starts" : "in"));
  const [values, setValues] = useState<string[]>(initialValue?.values ?? []);
  const [error, setError] = useState<string>();

  const onApplyHandler = useCallback(() => {
    const trimmed = values.map(v => v.trim()).filter(Boolean);
    if (!trimmed.length) {
      setError(isText ? "Enter a value first" : "Pick at least one value");
      return;
    }

    onApply({ operator, values: trimmed });
  }, [isText, operator, values, onApply]);

  return (
    <Wrapper>
      <Title>{definition.label}</Title>
      {isText ? (
        <>
          <Dropdown value={operator} options={TEXT_OPERATORS} onChange={({ value }) => setOperator(value)} />
          <InputText
            autoFocus
            value={values[0] ?? ""}
            onChange={({ target }) => setValues([target.value])}
            onKeyPress={({ key }) => key === "Enter" && onApplyHandler()}
          />
        </>
      ) : (
        <SelectValues definition={definition} values={values} onChange={setValues} />
      )}
      {error && <Hint $error>{error}</Hint>}
      <Footer>
        <Button label="Back" icon={IconType.Previous} buttonType={ButtonType.Text} onClick={onBack} />
        <Actions>
          <Button
            label="Cancel"
            ariaLabel="cancel filter"
            buttonType={ButtonType.Outlined}
            actionType={ButtonActionType.Secondary}
            icon={IconType.Close}
            iconPosition="right"
            onClick={onCancel}
          />
          <Button
            label="Apply"
            ariaLabel="apply filter"
            buttonType={ButtonType.Raised}
            icon={IconType.Ok}
            iconPosition="right"
            onClick={onApplyHandler}
          />
        </Actions>
      </Footer>
    </Wrapper>
  );
};

export const TableFilterEditor: FC<{
  definitions: TableFilterDefinition[];
  tableFilters: TableFilters;
  initialField?: string;
  onApply: (field: string, value: TableFilterValue) => void;
  onCancel: () => void;
}> = ({ definitions, tableFilters, initialField, onApply, onCancel }) => {
  const [field, setField] = useState(initialField);
  const definition = definitions.find(d => d.field === field);

  if (!definition) {
    return <FieldPicker definitions={definitions} tableFilters={tableFilters} onPick={setField} />;
  }

  return (
    <ValueEditor
      key={definition.field}
      definition={definition}
      initialValue={tableFilters[definition.field]}
      onBack={() => setField(undefined)}
      onCancel={onCancel}
      onApply={value => onApply(definition.field, value)}
    />
  );
};
